import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../App';
import AuthShell from '../components/auth/AuthShell';
import InvitePreview from '../components/auth/InvitePreview';
import './LoginLanding.css';

const BACKEND_URL = window.location.origin;

export default function LoginLanding() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState(null);

  const handleInviteResolved = useCallback((data) => {
    if (data?.invite?.email) {
      setInvitedEmail(data.invite.email);
      setEmail((current) => current || data.invite.email);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Giriş başarısız');
        setSubmitting(false);
        return;
      }
      const data = await response.json();
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
      }
      toast.success('Giriş başarılı!');
      await checkAuth();

      // 1) URL'den gelen davet (verify-email öncesi /register?invite=...
      //    ile başlamış kullanıcı login formuna URL'den geliyorsa)
      if (inviteToken) {
        navigate(`/invite/${inviteToken}?auto=1`, { replace: true });
        return;
      }
      // 2) DB'de saklı pendingInviteToken (kullanıcı kayıt sırasında invite
      //    ile geldi, e-posta doğrulamasından sonra şifresiyle giriş yaptı —
      //    auto-session kaldırıldığı için bu yol artık standart)
      const pending = data?.user?.pendingInviteToken;
      if (pending) {
        navigate(`/invite/${pending}?auto=1`, { replace: true });
        return;
      }
      const redirectUrl = localStorage.getItem('redirectAfterLogin');
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectUrl || '/dashboard', { replace: true });
    } catch (err) {
      toast.error('Giriş başarısız');
      setSubmitting(false);
    }
  };

  const banner = inviteToken ? (
    <InvitePreview token={inviteToken} onResolved={handleInviteResolved} />
  ) : null;

  return (
    <AuthShell banner={banner}>
      <div className="ll-hero" style={{ marginBottom: 28 }}>
        <h1>Hoş geldin.</h1>
        <p className="ll-sub">
          {inviteToken
            ? 'Giriş yap, davete devam et.'
            : 'Hesabına giriş yap, kaldığın yerden devam et.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} autoComplete="on" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div className="ll-label"><span>E-posta</span></div>
          <input
            className="ll-field-input"
            type="email"
            name="email"
            placeholder="ad.soyad@onxflow.co"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
          />
          {invitedEmail && email && email.toLowerCase() !== invitedEmail.toLowerCase() && (
            <div style={{ fontSize: 11, color: 'var(--warn, #d9a23a)', marginTop: 4 }}>
              Davet {invitedEmail} adresi için. Farklı e-posta ile giriş yaparsan daveti kabul edemezsin.
            </div>
          )}
        </div>

        <div>
          <div className="ll-label">
            <span>Şifre</span>
            <a href="#sifremi-unuttum" tabIndex={-1} onClick={(e) => e.preventDefault()}>Şifremi unuttum</a>
          </div>
          <div className="ll-password-wrap">
            <input
              className="ll-field-input"
              type={showPw ? 'text' : 'password'}
              name="password"
              placeholder="••••••••••"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ paddingRight: 42 }}
              data-testid="login-password-input"
            />
            <button
              type="button"
              className="ll-toggle-eye"
              aria-label={showPw ? 'Şifreyi gizle' : 'Şifreyi göster'}
              onClick={() => setShowPw((v) => !v)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </div>
        </div>

        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4, userSelect: 'none' }}
          onClick={(e) => { e.preventDefault(); setRemember((v) => !v); }}
        >
          <span className="ll-check" data-on={remember ? 'true' : 'false'}>
            {remember && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l4.5 4.5L19 7" />
              </svg>
            )}
          </span>
          <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>Bu cihazda hatırla</span>
        </label>

        <button
          className="ll-primary"
          type="submit"
          style={{ marginTop: 8 }}
          disabled={submitting}
          data-testid="login-submit-button"
        >
          <span>{submitting ? 'Giriş yapılıyor…' : 'Giriş yap'}</span>
          {!submitting && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" />
              <path d="M13 6l6 6-6 6" />
            </svg>
          )}
        </button>

        <div className="ll-divider mono" style={{ marginTop: 8 }}>VEYA</div>

        <Link
          to={inviteToken ? `/register?invite=${encodeURIComponent(inviteToken)}` : '/register'}
          className="ll-ghost"
          style={{ textDecoration: 'none' }}
          data-testid="login-register-link"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M19 8v6" />
            <path d="M22 11h-6" />
          </svg>
          <span>Hesap oluştur</span>
        </Link>
      </form>
    </AuthShell>
  );
}
